import { useMemo, useState } from 'react';
import { Pressable, ScrollView, Text, useWindowDimensions, View } from 'react-native';
import Animated, { Easing, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { useRouter } from 'expo-router';
import { StyleSheet } from 'react-native-unistyles';
import { Query } from '@/utils/react-query/Queries';
import ContentLoader from '@/components/General/ContentLoader';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import EventInstanceCard from '@/components/Events/EventInstanceCard';
import FeatureIcon from '@/components/General/FeatureIcon';
import CutCard from '@/components/General/CutCard';
import Unicon from '@/components/General/Unicon';
import dashboardStyles from './dashboardStyles';

const DAY_STEP = 54;

const startOfDay = (date) => {
    const value = new Date(date);
    value.setHours(0, 0, 0, 0);
    return value;
};

const addDays = (date, days) => {
    const value = startOfDay(date);
    value.setDate(value.getDate() + days);
    return value;
};

const isSameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const getDayKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const DashboardEventsSection = () => {
    const router = useRouter();
    const { width } = useWindowDimensions();
    const [selectedDate, setSelectedDate] = useState(() => startOfDay(new Date()));
    const slideOffset = useSharedValue(0);
    // Measure the row itself — on web the side nav shrinks the content area,
    // so window width overstates available space. Window width is only a
    // fallback for the first frame before layout is measured.
    const [scrollerWidth, setScrollerWidth] = useState(0);
    const basis = scrollerWidth > 0 ? scrollerWidth : width;
    const maxDays = Math.floor((basis - 60 + 6) / DAY_STEP); // two 30px nav buttons + gaps
    const oddDays = Math.max(3, maxDays % 2 === 0 ? maxDays - 1 : maxDays);
    const sideCount = Math.max(1, Math.min(3, (oddDays - 1) / 2));
    const today = startOfDay(new Date());
    const isToday = isSameDay(selectedDate, today);
    const dateKey = getDayKey(selectedDate);

    const { data: events = [], isLoading } = Query.eventInstances.forDate({ date: selectedDate, dateKey });

    const visibleDays = useMemo(() => Array.from(
        { length: sideCount * 2 + 1 },
        (_, index) => addDays(selectedDate, index - sideCount)
    ), [selectedDate, sideCount]);

    const weekStyle = useAnimatedStyle(() => ({
        transform: [{ translateX: slideOffset.value }],
    }));

    const selectDate = (date) => {
        const nextDate = startOfDay(date);
        const difference = Math.round((nextDate - selectedDate) / 86400000);
        if (!difference) return;

        setSelectedDate(nextDate);
        slideOffset.value = Math.sign(difference) * Math.min(Math.abs(difference), sideCount) * DAY_STEP;
        slideOffset.value = withTiming(0, { duration: 220, easing: Easing.out(Easing.cubic) });
    };

    const title = isToday
        ? 'Today'
        : selectedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

    return (
        <CutCard testID="dashboard-section-upcomingEvents" style={[styles.card, dashboardStyles.card]} contentStyle={dashboardStyles.content}>
            <View style={[styles.header, dashboardStyles.header]}>
                <View style={[styles.titleRow, dashboardStyles.titleRow]}>
                    <FeatureIcon feature="events" />
                    <Text style={[styles.title, dashboardStyles.title]}>{title}</Text>
                </View>
                <Pressable hitSlop={20} onPress={() => router.push('/events')} style={styles.linkBtn}>
                    <Text style={styles.linkText}>Calendar</Text>
                    <Unicon name="chevron-forward-outline" size={12} colorVariant="primary" />
                </Pressable>
            </View>

            <View style={styles.weekScroller} onLayout={(e) => setScrollerWidth(e.nativeEvent?.layout?.width || 0)}>
                <Pressable
                    accessibilityRole="button"
                    accessibilityLabel="Previous day"
                    testID="dashboard-events-previous-day"
                    onPress={() => selectDate(addDays(selectedDate, -1))}
                    style={styles.navButton}
                >
                    <Unicon name="chevron-back-outline" size={16} colorVariant="textSecondary" />
                </Pressable>

                <Animated.View style={[styles.daysRow, weekStyle]}>
                    {visibleDays.map((day) => {
                        const selected = isSameDay(day, selectedDate);
                        const isTodayDay = isSameDay(day, today);
                        return (
                            <Pressable
                                key={getDayKey(day)}
                                accessibilityRole="button"
                                accessibilityLabel={day.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                                testID={`dashboard-events-day-${getDayKey(day)}`}
                                onPress={() => selectDate(day)}
                                style={[
                                    styles.dayButton,
                                    isTodayDay && styles.todayButton,
                                    selected && styles.selectedDayButton,
                                ]}
                            >
                                <Text style={[styles.weekdayText, isTodayDay && styles.todayText, selected && styles.selectedDayText]}>
                                    {day.toLocaleDateString('en-US', { weekday: 'short' })}
                                </Text>
                                <Text style={[styles.dayText, isTodayDay && styles.todayText, selected && styles.selectedDayText]}>
                                    {day.getDate()}
                                </Text>
                                {isTodayDay && <View style={[styles.todayDot, selected && styles.selectedTodayDot]} />}
                            </Pressable>
                        );
                    })}
                </Animated.View>

                <Pressable
                    accessibilityRole="button"
                    accessibilityLabel="Next day"
                    testID="dashboard-events-next-day"
                    onPress={() => selectDate(addDays(selectedDate, 1))}
                    style={styles.navButton}
                >
                    <Unicon name="chevron-forward-outline" size={16} colorVariant="textSecondary" />
                </Pressable>
            </View>

            {isLoading ? (
                <View style={styles.loadingWrap}>
                    <ContentLoader containerHeight={180} />
                </View>
            ) : events.length > 0 ? (
                <ScrollView
                    testID="dashboard-events-scroll"
                    style={dashboardStyles.scroll}
                    contentContainerStyle={styles.eventsContent}
                    showsVerticalScrollIndicator={false}
                    keyboardShouldPersistTaps="handled"
                    nestedScrollEnabled
                >
                    {events.map((event) => <EventInstanceCard key={event.id} instance={event} variant="alternate" />)}
                </ScrollView>
            ) : (
                <View style={styles.emptyWrap}>
                    <EmptyListPlaceholder size={90} type="complete" message={isToday ? 'No events today' : 'No events on this date'} spacing={8} />
                </View>
            )}
        </CutCard>
    );
};

const styles = StyleSheet.create(({ colors, fonts, spacing, typography }) => ({
    card: {
        padding: spacing.md,
        paddingBottom: spacing.xs,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    titleRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    title: {
        fontFamily: fonts.bold,
    },
    linkBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 2,
        paddingVertical: spacing.xs,
        paddingHorizontal: spacing.xs + 2,
        borderRadius: 6,
        _web: { _hover: { backgroundColor: colors.hover } },
    },
    linkText: {
        color: colors.primary,
        fontSize: typography.caption,
        fontFamily: fonts.heading,
    },
    weekScroller: {
        flexShrink: 0,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: spacing.sm,
    },
    daysRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        flex: 1,
    },
    navButton: {
        width: 30,
        height: 30,
        borderRadius: 10,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.surfaceVariant,
        _web: { _hover: { backgroundColor: colors.hoverStrong } },
    },
    dayButton: {
        width: 48,
        flexShrink: 1,
        minWidth: 40,
        height: 58,
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        _web: {
            transition: 'background-color 0.15s ease',
            _hover: { backgroundColor: colors.hover },
        },
    },
    todayButton: {
        backgroundColor: colors.eventsContainer,
    },
    selectedDayButton: {
        backgroundColor: colors.events,
    },
    weekdayText: {
        fontSize: typography.micro,
        fontFamily: fonts.body,
        color: colors.textTertiary,
        textTransform: 'uppercase',
    },
    dayText: {
        fontSize: typography.h3,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    todayText: {
        color: colors.events,
    },
    selectedDayText: {
        color: colors.onEvents,
    },
    todayDot: {
        width: 4,
        height: 4,
        borderRadius: 2,
        backgroundColor: colors.events,
        marginTop: 1,
    },
    selectedTodayDot: {
        backgroundColor: colors.onEvents,
    },
    loadingWrap: {
        justifyContent: 'center',
    },
    eventsContent: {
        paddingBottom: spacing.sm,
    },
    emptyWrap: {
        justifyContent: 'center',
    },
}));

export default DashboardEventsSection;
